import { memo } from 'preact/compat'
import { useLocation } from 'preact-iso'
import type { DashboardTopPerformingWebItemDto } from '@/types/dashboard'
import { Globe } from 'lucide-preact'
import { LeadGoalBar } from '@/components/common/LeadGoalBar'
import { getWebTypeMeta } from '@/constants/webTypes'
import { useCompany } from '@/hooks/useCompany'
import { routes } from '@/utils/routes'
import { rowActivation } from '@/utils/rowActivation'

interface TopPerformingWebsTileProps {
  webs: DashboardTopPerformingWebItemDto[] | null | undefined
  workspaceId: string
  campaignId?: string
  limit?: number
  className?: string
  loading?: boolean
  error?: string | null
  /** The server could not compute this section — shown as broken, not as empty. */
  failed?: boolean
  /** Suppresses navigation while the dashboard is showing mock data. */
  mock?: boolean
}

function percentFrom(web: DashboardTopPerformingWebItemDto): number | undefined {
  if (web.LeadGoal > 0) {
    return (web.LeadsGenerated / Math.max(1, web.LeadGoal)) * 100
  }
  return undefined
}

export const TopPerformingWebsTile = memo(({ webs, workspaceId, campaignId, limit = 5, className = '', loading = false, error = null, failed = false, mock = false }: TopPerformingWebsTileProps) => {
  const { companyId } = useCompany()
  const location = useLocation()

  return (
    <div className={`tile flex flex-col ${className}`.trim()}>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
          Top Performing Webs
        </h3>
        <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
      </div>
      {loading && (
        <ul className="space-y-4">
          {Array.from({ length: limit }).map((_, i) => (
            <li key={i} className="flex flex-col gap-2 row-hover p-2 rounded-md">
              <div className="flex items-center justify-between">
                <div className="h-3 w-44 rounded bg-shimmer-light animate-shimmer" />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-16 rounded bg-shimmer-light animate-shimmer" />
                  <div className="h-5 w-14 rounded-full bg-shimmer-light animate-shimmer" />
                </div>
              </div>
              <div className="h-2 w-full rounded bg-shimmer-light animate-shimmer" />
              <div className="flex items-center gap-3 text-2xs">
                <div className="h-3 w-14 rounded bg-shimmer-light animate-shimmer" />
              </div>
            </li>
          ))}
        </ul>
      )}
      {!loading && error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
      {!loading && !error && failed && (
        <div className="text-sm text-red-600 dark:text-red-400">Couldn't load top performing webs</div>
      )}
      {!loading && !error && !failed && webs && webs.length === 0 && (
        <div className="text-sm text-slate-500 dark:text-slate-400">No web data</div>
      )}
      {!loading && !error && !failed && webs && webs.length > 0 && (
        <ul className="space-y-4">
          {webs.map((w: DashboardTopPerformingWebItemDto) => {
            const pct = percentFrom(w)
            const meta = getWebTypeMeta(w.Type)
            // Mock rows carry fabricated ids, and a row cannot navigate without the company and
            // workspace that build the route — either way there is nothing to activate.
            const canNavigate = !mock && !!companyId && !!workspaceId
            const activation = rowActivation(
              canNavigate
                ? () => location.route(routes.scoped(companyId!, workspaceId).web(campaignId || w.CampaignId, w.WebId))
                : null,
            )
            return (
              <li key={w.WebId}>
                <div
                  {...activation}
                  className={`flex flex-col gap-2 p-2 rounded-md ${activation ? 'row-hover cursor-pointer' : ''}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={w.Title}>{w.Title}</span>
                      <span className={`pill text-2xs font-medium ${meta.chipClass}`} title={w.Type}>{w.Type || 'Unknown'}</span>
                    </div>
                    <div className="text-xs2 font-semibold text-green-600 dark:text-green-400 whitespace-nowrap" title="Leads Generated">
                      {w.LeadsGenerated.toLocaleString()} leads
                      {w.LeadGoal > 0 && (
                        <span className="ml-1 opacity-70">/ {w.LeadGoal.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  {w.LeadGoal > 0 && (
                    <LeadGoalBar sourced={w.LeadsGenerated} goal={w.LeadGoal} mode="simple" animate={true} animationMs={600} capAt100={true} ariaLabel={`Lead goal progress for ${w.Title}`} glowDurationMs={3500} />
                  )}
                  <div className="flex items-center gap-3 text-2xs text-slate-600 dark:text-slate-300">
                    {pct !== undefined && (
                      <span className="font-medium" title="Percent of goal">{Math.min(100, Math.round(pct))}% of goal</span>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
})
